import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';

const PERMISSOES = [
  { value: 'visualizar', label: 'Visualizar' },
  { value: 'editar', label: 'Editar' }
];

export default function CompartilharDialog({
  open,
  onClose,
  tituloItem,
  tipo,
  compartilhamentos = [],
  onCompartilhar,
  onAlterarPermissao,
  onRemover,
  listarUsuarios
}) {
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissao, setPermissao] = useState('visualizar');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const list = await listarUsuarios();
        if (!cancelled) setUsuarios(list || []);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listarUsuarios]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setPermissao('visualizar');
    }
  }, [open]);

  const idsCompartilhados = new Set((compartilhamentos || []).map((c) => c.usuarioId));
  const usuariosDisponiveis = usuarios.filter((u) => !idsCompartilhados.has(u.id));

  const handleAdicionar = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await onCompartilhar(selectedUser.id, permissao);
      setSelectedUser(null);
      setPermissao('visualizar');
    } finally {
      setSaving(false);
    }
  };

  const tipoLabel = tipo === 'nota' ? 'nota' : 'tarefa';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Compartilhar {tipoLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tituloItem}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Usuários da mesma unidade. Você define se a pessoa pode editar ou apenas visualizar.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'flex-end' }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 180 }}
            size="small"
            options={usuariosDisponiveis}
            loading={loadingUsers}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            getOptionLabel={(o) => (o ? `${o.nome} (${o.email})` : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuário"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Permissão</InputLabel>
            <Select
              label="Permissão"
              value={permissao}
              onChange={(e) => setPermissao(e.target.value)}
            >
              {PERMISSOES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<PersonAddOutlinedIcon />}
            disabled={!selectedUser || saving}
            onClick={handleAdicionar}
            sx={{ textTransform: 'none' }}
          >
            Adicionar
          </Button>
        </Box>

        {(compartilhamentos || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ninguém compartilhado ainda.
          </Typography>
        ) : (
          <List dense disablePadding>
            {(compartilhamentos || []).map((c) => (
              <ListItem
                key={c.usuarioId}
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => onRemover(c.usuarioId)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ px: 0 }}
              >
                <ListItemText
                  primary={c.nome || c.email}
                  secondary={c.email}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                  <Select
                    value={c.permissao || 'visualizar'}
                    onChange={(e) => onAlterarPermissao(c.usuarioId, e.target.value)}
                  >
                    {PERMISSOES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
